import { Box, Paper } from '@mui/material';
import ConfirmCode from 'containers/login/components/ConfirmCode';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { resetAllValidationInfo, resetValidationInfo, setValidationInfo } from 'redux/actions/internal/general/validation';
import firebase, { secondaryAuth } from 'config/firebase';
import { useSignInProcess } from 'hooks/useSignInProcess';
import { useLoginFlow } from 'hooks/useLoginFlow';
import { resetUsersLoginInfo } from 'redux/actions/internal/general/users';
import { RootState } from 'redux/reducers';
import { AlertDialog } from '../../../components/organisms/Dialogs';
import { useAuth } from 'context/authentication';
import { useCustomerLoginStream } from 'hooks/useCustomerInfo';
import { useFirebaseErrorMessages } from 'hooks/useFirebaseErrorMessages';

interface IConfirmCodePaperProps {
    onLogin?: (user: any, register?: boolean) => void;
}

const ConfirmCodePaper = ({ onLogin }: IConfirmCodePaperProps) => {
    const dispatch = useDispatch();
    const signInProcess = useSignInProcess();
    const authentication = useLoginFlow();
    const [loading, setLoading] = useState<boolean>(false);
    const usersLoginInfo = useSelector((state: RootState) => state.usersLoginInfo);
    const [alertOpen, setAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>('');
    const [showResend, setShowResend] = useState<boolean>(false);

    const firebaseMessages = useFirebaseErrorMessages();

    const { linkWithEmailCredential, confirmResult, postUserToken, loginWithPersistedCredential, loginWithCredential } =
        useAuth();
    const { registerCustomer } = useCustomerLoginStream();

    const handleSubmitCode = (code: string) => {
        setLoading(true);
        dispatch(resetValidationInfo('CODE_CONFIRMATION'));
        confirmResult(code)
            .then(result => {
                setLoading(false);
                if (signInProcess.hasBeenThrough('REGISTER_INFO', 'LOGIN_WITH_EMAIL')) {
                    linkWithEmail();
                } else if (signInProcess.hasBeenThrough('SOCIAL_GOOGLE')) {
                    loginWithPersistedCredential().then(() => {
                        registerCustomer(secondaryAuth.currentUser.uid)
                            .then(() => {
                                setLoading(false);
                                postUserToken(result.user);
                                onLogin?.(result.user, true);
                                dispatch(resetUsersLoginInfo());
                                authentication.closeLogin();
                            })
                            .catch(error => {
                                dispatch(setValidationInfo({ hasError: true, message: error.message }, 'CODE_CONFIRMATION'));
                            });
                    });
                } else {
                    if (signInProcess.hasBeenThrough('LOGIN_WITH_PHONE', 'REGISTER_INFO')) {
                        registerCustomer(result.user.uid)
                            .then(() => {
                                setLoading(false);
                                postUserToken(result.user);
                                onLogin?.(result.user, true);
                                dispatch(resetUsersLoginInfo());
                                authentication.closeLogin();
                            })
                            .catch(error => {
                                dispatch(setValidationInfo({ hasError: true, message: error.message }, 'CODE_CONFIRMATION'));
                            });
                    } else {
                        setLoading(false);
                        postUserToken(result.user);
                        onLogin?.(result.user);
                        dispatch(resetUsersLoginInfo());
                        authentication.closeLogin();
                    }

                    // db.doc(`/customers/${result.user.uid}`)
                    //     .get()
                    //     .then(data => {
                    //         console.log('before credential create');
                    //         const credential = firebase.auth.PhoneAuthProvider.credential(
                    //             window.confirmationResult.verificationId,
                    //             code
                    //         );
                    //         console.log('after credential create');
                    //         if (data.exists) {
                    //             loginWithCredential(credential)
                    //                 .then(() => {
                    //                     postUserToken(result.user);
                    //                     onLogin?.(result.user);
                    //                     dispatch(resetUsersLoginInfo());
                    //                     authentication.closeLogin();
                    //                 })
                    //                 .catch(error => {
                    //                     dispatch(
                    //                         setValidationInfo({ hasError: true, message: error.message }, 'CODE_CONFIRMATION')
                    //                     );
                    //                 });
                    //         } else {
                    //             console.log('before persist credential', credential);
                    //             persistCredential(credential);
                    //             signInProcess.nextStep('REGISTER_INFO');
                    //         }
                    //     });
                }
            })
            .catch(function (reason: firebase.auth.AuthError) {
                if (reason.code === 'auth/credential-already-in-use') {
                    setAlertMessage(firebaseMessages[reason.code]);
                    setAlertOpen(true);
                } else if (reason.code === 'auth/invalid-verification-code' || reason.code === 'auth/code-expired') {
                    setShowResend(true);
                    dispatch(setValidationInfo({ hasError: true, message: firebaseMessages[reason.code] }, 'CODE_CONFIRMATION'));
                } else {
                    dispatch(setValidationInfo({ hasError: true, message: reason.message }, 'CODE_CONFIRMATION'));
                }
                setLoading(false);
            });
    };

    const linkWithEmail = () => {
        linkWithEmailCredential(usersLoginInfo.data.email, usersLoginInfo.data.password)
            .then(userCredential => {
                loginWithCredential(
                    firebase.auth.EmailAuthProvider.credential(usersLoginInfo.data.email, usersLoginInfo.data.password)
                )
                    .then(() => {
                        registerCustomer(userCredential.user.uid)
                            .then(() => {
                                setLoading(false);
                                postUserToken(userCredential.user);
                                onLogin?.(userCredential.user, true);
                                authentication.closeLogin();
                            })
                            .catch(error => {
                                dispatch(setValidationInfo({ hasError: true, message: error.message }, 'CODE_CONFIRMATION'));
                            });
                    })
                    .catch(error => {
                        dispatch(setValidationInfo({ hasError: true, message: error.message }, 'CODE_CONFIRMATION'));
                    });
            })
            .catch((reason: firebase.auth.AuthError) => {
                if (reason.code === 'auth/provider-already-linked') {
                    setAlertMessage(`We recognize you! You already have an account.`);
                    setAlertOpen(true);
                } else {
                    dispatch(setValidationInfo({ hasError: true, message: reason.message }, 'CODE_CONFIRMATION'));
                }
                setLoading(false);
            });
    };

    const handleAlertClose = () => {
        setAlertOpen(false);
        authentication.closeLogin();
    };

    const handleSignIn = () => {
        setAlertOpen(false);
        dispatch(resetAllValidationInfo());
        dispatch(resetUsersLoginInfo());
        signInProcess.goThrough([], 'LOGIN_WITH_PHONE');
    };

    return (
        <Paper
            sx={{
                backgroundColor: theme => theme.palette.background.paper,
                boxShadow: theme => theme.shadows[5],
                width: '500px',
                minHeight: '50vh',
                display: 'flex',
                flexDirection: 'column',
                maxWidth: { xs: '90vw', md: 'unset' },
            }}
            style={{ justifyContent: 'center' }}
        >
            <Box
                sx={{
                    py: 4,
                    px: { xs: 2, lg: 10 },
                }}
            >
                <ConfirmCode onComplete={handleSubmitCode} loading={loading} />
                {/* <Box mt={2} display="flex" justifyContent="center">
                    <BasicButton>
                        <NormalText>Resend code</NormalText>
                    </BasicButton>
                </Box> */}
            </Box>
            <AlertDialog
                open={alertOpen}
                handleClose={handleAlertClose}
                title="We recognize you!"
                actions={[
                    {
                        title: 'Close',
                        color: 'primary',
                        autofocus: false,
                        handler: handleAlertClose,
                    },
                    { title: 'Sign in', color: 'primary', autofocus: true, handler: handleSignIn },
                ]}
            >
                {alertMessage}
            </AlertDialog>
        </Paper>
    );
};

export default ConfirmCodePaper;
